'use client'

import ConfirmModal from '@/components/confirm-modal'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    addExamResult,
    CreateExamResultData,
    deleteExamResult,
    Exam,
    ExamResult,
    getExam,
    printExamResults,
    updateExamResult,
    uploadExamResultAttachment,
} from '@/lib/api/exams/exam-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useRef, useState } from 'react'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'
import {
    MdArrowBack,
    MdBiotech,
    MdCheck,
    MdClose,
    MdCloudUpload,
    MdDelete,
    MdDownload,
    MdEdit,
    MdInsertDriveFile,
    MdPerson,
    MdPictureAsPdf,
    MdPrint,
    MdSave
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface ResultFormData {
    result: string
    observations: string
    professionalId: string
}

const defaultResultForm: ResultFormData = {
    result: '',
    observations: '',
    professionalId: '',
}

export default function ExamResultsPage() {
    const navigate = useNavigate()
    const { user, isProfessional } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('bookings', 'exams')
    const { id } = useParams<{ id: string }>()

    const [exam, setExam] = useState<Exam | null>(null)
    const [loading, setLoading] = useState(true)

    // Tab state - index of the active exam catalog item
    const [activeTabIndex, setActiveTabIndex] = useState(0)

    // Form state per tab
    const [formData, setFormData] = useState<ResultFormData>(defaultResultForm)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [saving, setSaving] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<string | null>(null)
    const [editing, setEditing] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // PDF export / print
    const [printing, setPrinting] = useState(false)
    const [showSignModal, setShowSignModal] = useState(false)

    // Delete result
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deletingResult, setDeletingResult] = useState(false)

    useEffect(() => {
        if (id) {
            loadExam()
        }
    }, [id])

    // When tab changes or exam loads, sync form with existing result
    useEffect(() => {
        syncFormWithResult()
    }, [activeTabIndex, exam])

    const loadExam = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getExam(parseInt(id, 10))
            if (response.success && response.result) {
                setExam(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar exame')
                navigate('/bookings/exams')
            }
        } catch (error) {
            toast.error('Erro ao carregar exame')
            navigate('/bookings/exams')
        } finally {
            setLoading(false)
        }
    }

    const getActiveItem = () => {
        return exam?.items?.[activeTabIndex]
    }

    const getResultForItem = (catalogId: number): ExamResult | undefined => {
        return exam?.results?.find(r => r.catalogId === catalogId)
    }

    const getActiveResult = (): ExamResult | undefined => {
        const item = getActiveItem()
        if (!item) return undefined
        return getResultForItem(item.catalogId)
    }

    const syncFormWithResult = () => {
        const result = getActiveResult()
        if (result) {
            setFormData({
                result: result.result || '',
                observations: result.observations || '',
                professionalId: result.professionalId?.toString() || '',
            })
            setEditing(false)
        } else {
            // Quem está logado como profissional é o responsável pelo resultado:
            // não há o que escolher, e o seletor fica escondido.
            setFormData({
                ...defaultResultForm,
                professionalId:
                    isProfessional && user?.professionalId
                        ? String(user.professionalId)
                        : '',
            })
            setEditing(true)
        }
        setSelectedFiles([])
        setUploadProgress(null)
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files && files.length > 0) {
            const fileArray = Array.from(files)
            setSelectedFiles(prev => [...prev, ...fileArray])
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    }

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const handleSave = async () => {
        if (!exam || !id) return

        const item = getActiveItem()
        if (!item) return

        if (!formData.professionalId) {
            toast.warning('Selecione o profissional responsável')
            return
        }

        if (!formData.result.trim() && selectedFiles.length === 0) {
            toast.warning('Informe o resultado ou anexe pelo menos um arquivo')
            return
        }

        setSaving(true)
        try {
            const examId = parseInt(id, 10)
            const existingResult = getActiveResult()

            if (existingResult) {
                // Update
                const response = await updateExamResult(examId, existingResult.id, {
                    result: formData.result.trim() || undefined,
                    observations: formData.observations.trim() || undefined,
                })

                if (!response.success) {
                    toast.error(response.error || 'Erro ao atualizar resultado')
                    return
                }

                // Upload new files
                if (selectedFiles.length > 0) {
                    await uploadFiles(examId, existingResult.id)
                }

                toast.success('Resultado atualizado!')
            } else {
                // Create
                const createData: CreateExamResultData = {
                    catalogId: item.catalogId,
                    result: formData.result.trim() || undefined,
                    observations: formData.observations.trim() || undefined,
                    professionalId: parseInt(formData.professionalId, 10),
                }

                const response = await addExamResult(examId, createData)
                if (!response.success || !response.result) {
                    toast.error(response.error || 'Erro ao criar resultado')
                    return
                }

                // Upload files
                if (selectedFiles.length > 0) {
                    await uploadFiles(examId, response.result.id)
                }

                toast.success('Resultado salvo!')
            }

            setSelectedFiles([])
            setEditing(false)
            await loadExam()
        } catch (error) {
            toast.error('Erro ao salvar resultado')
        } finally {
            setSaving(false)
            setUploadProgress(null)
        }
    }

    const uploadFiles = async (examId: number, resultId: number) => {
        for (let i = 0; i < selectedFiles.length; i++) {
            setUploadProgress(`Enviando arquivo ${i + 1} de ${selectedFiles.length}...`)
            try {
                await uploadExamResultAttachment(
                    examId,
                    resultId,
                    selectedFiles[i],
                    user?.name || 'Usuário',
                    selectedFiles[i].name,
                )
            } catch (err) {
                toast.error(`Erro ao enviar arquivo: ${selectedFiles[i].name}`)
            }
        }
    }

    const handlePrint = async () => {
        if (!exam) return
        setPrinting(true)
        try {
            await printExamResults(exam.id)
            toast.success('PDF gerado com sucesso!')
        } catch (error) {
            toast.error('Erro ao gerar PDF para impressão')
        } finally {
            setPrinting(false)
        }
    }

    const confirmDeleteResult = async () => {
        const result = getActiveResult()
        if (!result || !id) return

        setDeletingResult(true)
        try {
            const response = await deleteExamResult(parseInt(id, 10), result.id)
            if (response.success) {
                toast.success('Resultado excluído!')
                await loadExam()
            } else {
                toast.error(response.error || 'Erro ao excluir resultado')
            }
        } catch (error) {
            toast.error('Erro ao excluir resultado')
        } finally {
            setDeletingResult(false)
            setShowDeleteModal(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando exame...</p>
            </div>
        )
    }

    if (!exam || !exam.items || exam.items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <MdBiotech className="text-4xl text-base-content/30" />
                <p className="text-base-content/60">Nenhum exame encontrado</p>
                <button onClick={() => navigate('/bookings/exams')} className="btn btn-secondary">
                    Voltar para Exames
                </button>
            </div>
        )
    }

    const activeItem = getActiveItem()
    const activeResult = getActiveResult()
    const isReadonly = !editing && !!activeResult
    const canEdit = canCreate || canUpdate

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/bookings/exams/${exam.id}`)}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Resultados dos Exames
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {exam.client?.name || 'Paciente'}
                            {exam.items.length > 0 && (
                                ` - ${exam.items[0].catalog?.name || 'Item sem catálogo'}${
                                    exam.items.length > 1 ? ` +${exam.items.length - 1}` : ''
                                }`
                            )}
                        </p>
                    </div>
                </div>

                {/* Progress + PDF + Print */}
                <div className="flex items-center gap-4">
                    {exam.results && exam.results.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowSignModal(true)}
                                className="btn btn-outline btn-secondary btn-sm gap-2"
                                title="Exportar PDF com assinatura digital"
                            >
                                <MdPictureAsPdf className="text-lg" />
                                <span className="hidden sm:inline">Exportar PDF</span>
                            </button>
                            <button
                                onClick={handlePrint}
                                disabled={printing}
                                className="btn btn-outline btn-sm gap-2"
                                title="Imprimir com área de assinatura"
                            >
                                {printing ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdPrint className="text-lg" />
                                )}
                                <span className="hidden sm:inline">Imprimir</span>
                            </button>
                        </div>
                    )}
                    <div className="text-right">
                        <span className="text-sm text-base-content/60">Resultados</span>
                        <p className="font-bold text-lg">
                            {exam.items.filter(item => exam.results?.some(r => r.catalogId === item.catalogId)).length}
                            <span className="text-base-content/40">/{exam.items.length}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs per exam catalog item */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm overflow-hidden">
                <div className="tabs tabs-bordered flex-wrap px-2 py-2">
                    {exam.items.map((item, index) => {
                        const hasResult = exam.results?.some(r => r.catalogId === item.catalogId)
                        const isActive = activeTabIndex === index
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveTabIndex(index)}
                                className={`tab gap-2 ${isActive
                                    ? 'tab-active text-secondary font-semibold'
                                    : 'text-base-content/70 hover:text-base-content'
                                    }`}
                            >
                                {hasResult && <MdCheck className="text-success text-base" />}
                                <span>{item.catalog?.name || 'Item sem catálogo'}</span>
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Content for active tab */}
            {activeItem && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    {/* Tab header */}
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <MdBiotech className="text-xl text-secondary" />
                                <div>
                                    <h3 className="text-lg font-bold text-base-content">
                                        {activeItem.catalog.name}
                                    </h3>
                                    <p className="text-sm text-base-content/60">
                                        {activeItem.catalog.catalogType?.name || '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {activeResult && !editing && canEdit && (
                                    <>
                                        <button
                                            onClick={() => setEditing(true)}
                                            className="btn btn-sm btn-ghost gap-1"
                                        >
                                            <MdEdit className="text-base" />
                                            Editar
                                        </button>
                                        {canDelete && (
                                            <button
                                                onClick={() => setShowDeleteModal(true)}
                                                className="btn btn-sm btn-ghost text-error gap-1"
                                            >
                                                <MdDelete className="text-base" />
                                            </button>
                                        )}
                                    </>
                                )}
                                {activeResult && (
                                    <span className="badge badge-success text-white gap-1">
                                        <MdCheck className="text-sm" />
                                        Resultado registrado
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        {isReadonly && activeResult ? (
                            /* Read-only view */
                            <div className="space-y-4">
                                {activeResult.result && (
                                    <div>
                                        <h4 className="font-semibold text-base-content mb-2">Resultado</h4>
                                        <div className="p-4 bg-base-200/50 rounded-xl">
                                            <p className="text-base-content whitespace-pre-wrap">
                                                {activeResult.result}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {activeResult.observations && (
                                    <div>
                                        <h4 className="font-semibold text-base-content mb-2">Observações</h4>
                                        <div className="p-4 bg-base-200/50 rounded-xl">
                                            <p className="text-base-content/80 whitespace-pre-wrap">
                                                {activeResult.observations}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center gap-4 text-sm text-base-content/60">
                                    {activeResult.professional && (
                                        <div className="flex items-center gap-1">
                                            <MdPerson className="text-base" />
                                            <span>
                                                {activeResult.professional.name}
                                                {activeResult.professional.registrationNumber &&
                                                    ` - ${activeResult.professional.registrationNumber}`}
                                            </span>
                                        </div>
                                    )}
                                    <span>
                                        {format(new Date(activeResult.resultDate), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    </span>
                                </div>

                                {/* Existing attachments */}
                                {activeResult.attachments && activeResult.attachments.length > 0 && (
                                    <div>
                                        <h4 className="font-semibold text-base-content mb-2">
                                            Anexos ({activeResult.attachments.length})
                                        </h4>
                                        <div className="space-y-2">
                                            {activeResult.attachments.map((att, i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center gap-3 p-3 bg-base-200/50 rounded-lg border border-base-300/50"
                                                >
                                                    <MdInsertDriveFile className="text-lg text-secondary flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{att.fileName}</p>
                                                        {att.createdAt && (
                                                            <p className="text-xs text-base-content/40">
                                                                {format(new Date(att.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <a
                                                        href={att.filePath}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-ghost btn-sm btn-square"
                                                        title="Baixar"
                                                    >
                                                        <MdDownload className="text-lg" />
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Edit/Create form */
                            <div className="space-y-4">
                                {!isProfessional && (
                                    <ProfessionalSearchSelector
                                        value={formData.professionalId ? Number(formData.professionalId) : null}
                                        onChange={(id) => setFormData(prev => ({ ...prev, professionalId: id ? String(id) : '' }))}
                                        label="Profissional Responsável *"
                                        placeholder="Selecione..."
                                        disabled={!!activeResult}
                                    />
                                )}

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Resultado (opcional se for em arquivo)
                                        </span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        placeholder="Descreva o resultado do exame..."
                                        rows={6}
                                        value={formData.result}
                                        onChange={e => setFormData(prev => ({ ...prev, result: e.target.value }))}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Observações</span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        placeholder="Observações adicionais..."
                                        rows={3}
                                        value={formData.observations}
                                        onChange={e => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                                    />
                                </div>

                                {/* Existing attachments (when editing) */}
                                {activeResult?.attachments && activeResult.attachments.length > 0 && (
                                    <div>
                                        <label className="label">
                                            <span className="label-text font-medium">Anexos existentes</span>
                                        </label>
                                        <div className="space-y-2">
                                            {activeResult.attachments.map((att, i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center gap-3 p-3 bg-base-200/50 rounded-lg border border-base-300/50"
                                                >
                                                    <MdInsertDriveFile className="text-lg text-secondary flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{att.fileName}</p>
                                                    </div>
                                                    <a
                                                        href={att.filePath}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-ghost btn-sm btn-square"
                                                    >
                                                        <MdDownload className="text-lg" />
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* File Upload */}
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            {activeResult ? 'Adicionar novos anexos' : 'Anexos'}
                                        </span>
                                    </label>

                                    <div
                                        className="border-2 border-dashed border-base-300 rounded-xl p-6 text-center cursor-pointer hover:border-secondary/50 hover:bg-secondary/5 transition-colors"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <MdCloudUpload className="text-3xl mx-auto mb-2 text-base-content/40" />
                                        <p className="text-sm text-base-content/60">
                                            Clique para selecionar arquivos
                                        </p>
                                        <p className="text-xs text-base-content/40 mt-1">
                                            PDF, imagens, documentos...
                                        </p>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        multiple
                                        onChange={handleFileSelect}
                                    />

                                    {selectedFiles.length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            {selectedFiles.map((file, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center gap-3 p-3 bg-base-200/50 rounded-lg"
                                                >
                                                    <MdInsertDriveFile className="text-lg text-secondary flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{file.name}</p>
                                                        <p className="text-xs text-base-content/50">{formatFileSize(file.size)}</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(index)}
                                                        className="btn btn-ghost btn-xs btn-square text-error"
                                                    >
                                                        <MdClose className="text-base" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {uploadProgress && (
                                    <div className="flex items-center gap-2 text-sm text-info">
                                        <span className="loading loading-spinner loading-sm"></span>
                                        <span>{uploadProgress}</span>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex items-center justify-end gap-2 pt-2">
                                    {activeResult && (
                                        <button
                                            onClick={() => {
                                                syncFormWithResult()
                                                setEditing(false)
                                            }}
                                            className="btn btn-ghost"
                                            disabled={saving}
                                        >
                                            Cancelar
                                        </button>
                                    )}
                                    <button
                                        onClick={handleSave}
                                        className="btn btn-secondary gap-2"
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="loading loading-spinner loading-sm"></span>
                                                {uploadProgress ? 'Enviando...' : 'Salvando...'}
                                            </>
                                        ) : (
                                            <>
                                                <MdSave className="text-lg" />
                                                {activeResult ? 'Atualizar' : 'Salvar Resultado'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteResult}
                title="Excluir resultado"
                message={`Tem certeza que deseja excluir o resultado de "${activeItem?.catalog.name}"?`}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deletingResult}
            />

            {exam && (
                <SignDocumentModal
                    isOpen={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    documentType="EXAM_RESULTS"
                    documentId={exam.id}
                    documentTitle={`Resultados - ${exam.items?.map(i => (i.catalog?.name || 'Item sem catálogo')).join(', ') || exam.title}`}
                    onSigned={() => {
                        setShowSignModal(false)
                    }}
                />
            )}
        </div>
    )
}
